import { lazy } from 'react';

/**
 * Wraps React.lazy import factories with a single-use retry that force-reloads
 * the app when a stale chunk (JS or CSS) cannot be loaded after a deployment.
 *
 * Prevents the "ChunkLoadError: Loading CSS chunk XXX failed" loops that were
 * breaking Admin Dashboard / Guide lazy chunks for admins.
 */
const lazyWithRetry = (factory, key = 'chunk') => {
  const storageKey = `thryveloop_lazy_retry_${key}`;

  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      const message = error?.message || '';
      const lowerMessage = message.toLowerCase();
      const isChunkError =
        error?.name === 'ChunkLoadError' ||
        message.includes('Loading CSS chunk') ||
        message.includes('Loading chunk') ||
        message.includes('Failed to fetch dynamically imported module') ||
        lowerMessage.includes('error loading dynamically imported module') ||
        message.includes('Importing a module script failed');

      if (isChunkError && typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
        try {
          const lastAttempt = Number(sessionStorage.getItem(storageKey) || '0');
          const now = Date.now();

          if (!lastAttempt || now - lastAttempt > 2 * 60 * 1000) {
            sessionStorage.setItem(storageKey, String(now));
            console.warn(`[lazyWithRetry] ${key} chunk failed. Reloading to fetch a fresh bundle.`);
            window.location.reload();
          } else {
            // Clear the key so a future deploy can retry again.
            sessionStorage.removeItem(storageKey);
          }
        } catch (storageError) {
          console.warn('[lazyWithRetry] Failed to persist reload attempt:', storageError);
          window.location.reload();
        }
      }

      throw error;
    }
  });
};

export default lazyWithRetry;
